import { TemplateResult, html, unsafeCSS } from 'lit';
import Cookies from 'js-cookie';
import { property, state } from 'lit/decorators.js';
import { classMap } from 'lit/directives/class-map.js';
import { ScopedRegistryHost } from '@lit-labs/scoped-registry-mixin';
import { consume } from '@lit/context';
import { LeafElement } from '@cigna/dcws_leaf-web-components/components/leaf-element.js';
import { LeafButton } from '@cigna/dcws_leaf-web-components/components/button/button.component.js';
import { LeafInput } from '@cigna/dcws_leaf-web-components/components/input/input.component.js';
import { LeafLabel } from '@cigna/dcws_leaf-web-components/components/label/label.component.js';
import { LeafSpinner } from '@cigna/dcws_leaf-web-components/components/spinner/spinner.component.js';
import { I18nController } from '@cigna/dcws_leaf-web-components/utilities/i18n.js';
import { ICampaignInfo } from '../campaigns/interfaces.js';
import { dynamicLinksContext } from '../contexts.js';
import { updateDynamicLinks } from '../campaigns/link-service.js';
import styles from './medical-banner.scss?inline';

export class CHCMedicalBanner extends ScopedRegistryHost(LeafElement) {
  static override styles = unsafeCSS(styles);
  static elementDefinitions = {
    'leaf-button': LeafButton,
    'leaf-input': LeafInput,
    'leaf-label': LeafLabel,
    'leaf-spinner': LeafSpinner,
  };
  private readonly i18nController = new I18nController(this);

  @property()
  variant: 'supplemental' | 'dental' = 'supplemental';

  @property({ reflect: true })
  action = '';

  @consume({ context: dynamicLinksContext, subscribe: true })
  @state()
  private campaignsObj!: ICampaignInfo;

  get supplementalForm(): HTMLFormElement {
    // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
    return this.renderRoot.querySelector('.chcapp-medical-banner__supplemental-form')!;
  }

  protected override async firstUpdated() {
    await this.updateComplete;
    if (this.variant === 'supplemental') {
      this.supplementalForm.addEventListener('submit', (event: SubmitEvent) => {
        event.preventDefault();
        const form = event.target;
        if (form instanceof HTMLFormElement) {
          const formData = new FormData(form);
          const zip = String(formData.get('zip_code'));
          const updatedHref = updateDynamicLinks(this.action, this.campaignsObj);
          Cookies.set('campaignZipCode-cipublic', zip, { domain: 'cigna.com' });
          this.action = updatedHref.length ? updatedHref : this.action;
          window.open(this.action, '_blank');
        }
      });
    }
  }

  renderSupplementalForm(): TemplateResult {
    return html`
      <form class="chcapp-medical-banner__supplemental-form">
        <div>
          <leaf-label for="zip-input-id">${this.i18nController.term('zip')}</leaf-label>
          <leaf-input
            autocomplete="postal-code"
            name="zip_code"
            required
            type="tel"
            pattern="[0-9]{5}"
            id="zip-input-id"
          >
            <span slot="note">00000-0000</span>
          </leaf-input>
        </div>
        <leaf-button class="chcapp-medical-banner__supplemental-form-button" type="submit"
          >${this.i18nController.term('shop')}</leaf-button
        >
      </form>
    `;
  }

  renderDentalForm(): TemplateResult {
    const updatedHref = updateDynamicLinks(this.action, this.campaignsObj);
    return html`<div class="chcapp-medical-banner__dental-container">
      <slot name="dental-cta"></slot>
      <leaf-button href="${updatedHref.length ? updatedHref : this.action}" target="_blank"
        >${this.i18nController.term('shop')}</leaf-button
      >
    </div>`;
  }

  override render() {
    const componentClassName = {
      'chcapp-medical-banner': true,
    };

    return html`<div class=${classMap(componentClassName)}>
      ${this.variant === 'dental' ? this.renderDentalForm() : this.renderSupplementalForm()}
    </div> `;
  }
}
